import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFarms, useLots, useAnimals, createLotLocal, deleteLotLocal, deleteFarmLocal } from "@/lib/repo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ArrowLeft, Plus, ScanLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function FarmDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const farms = useFarms();
  const farm = farms.find((f) => f.id === id);
  const lots = useLots(id);
  const animals = useAnimals({ farm_id: id });
  const [open, setOpen] = useState(false);
  const [lotForm, setLotForm] = useState({ name: "", description: "" });

  const createLot = async () => {
    if (!lotForm.name) return toast.error("Nombre requerido");
    await createLotLocal(id, lotForm);
    setOpen(false); setLotForm({ name: "", description: "" });
    toast.success("Lote creado");
  };
  const deleteLot = async (lotId) => {
    if (!window.confirm("¿Eliminar lote?")) return;
    await deleteLotLocal(lotId);
  };
  const deleteFarm = async () => {
    if (!window.confirm("¿Eliminar finca? Los animales no se eliminan, pero quedarán huérfanos.")) return;
    await deleteFarmLocal(id); navigate("/fincas");
  };

  if (!farm) return null;
  const canEdit = user?.role === "owner";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/fincas")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold flex-1">{farm.name}</h1>
        <Button onClick={()=>navigate("/inventario/nuevo")} className="h-9 bg-emerald-700 hover:bg-emerald-800"><ScanLine size={14} className="mr-1"/>Inventariar</Button>
      </div>
      <Card>
        <CardContent className="p-5 grid grid-cols-3 gap-3 text-center">
          <div><div className="font-heading text-2xl font-semibold">{animals.length}</div><div className="text-xs text-muted-foreground">Animales</div></div>
          <div><div className="font-heading text-2xl font-semibold">{lots.length}</div><div className="text-xs text-muted-foreground">Lotes</div></div>
          <div><div className="font-heading text-2xl font-semibold">{farm.location || "-"}</div><div className="text-xs text-muted-foreground">Ubicación</div></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Lotes</CardTitle>
          {canEdit && (
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="farm-add-lot"><Plus size={14} className="mr-1"/>Lote</Button></SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-2xl">
                <SheetHeader><SheetTitle>Nuevo lote</SheetTitle></SheetHeader>
                <div className="py-3 space-y-3">
                  <div><Label>Nombre</Label><Input data-testid="farm-lot-name" value={lotForm.name} onChange={(e)=>setLotForm({ ...lotForm, name: e.target.value })} className="h-11" /></div>
                  <div><Label>Descripción</Label><Input value={lotForm.description} onChange={(e)=>setLotForm({ ...lotForm, description: e.target.value })} className="h-11" /></div>
                  <Button onClick={createLot} className="h-12 w-full bg-emerald-700 hover:bg-emerald-800" data-testid="farm-lot-submit">Guardar</Button>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </CardHeader>
        <CardContent className="p-0 divide-y divide-border">
          {lots.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin lotes en esta finca.</p>}
          {lots.map((l) => (
            <div key={l.id} className="p-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{l.name}</div>
                {l.description && <div className="text-xs text-muted-foreground">{l.description}</div>}
              </div>
              {canEdit && <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-50" onClick={()=>deleteLot(l.id)}><Trash2 size={14} /></Button>}
            </div>
          ))}
        </CardContent>
      </Card>

      {canEdit && (
        <div className="flex justify-end">
          <Button variant="ghost" className="text-red-700 hover:bg-red-50" onClick={deleteFarm} data-testid="farm-delete"><Trash2 size={14} className="mr-1"/>Eliminar finca</Button>
        </div>
      )}
    </div>
  );
}
